/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { MediaService } from './media.service';
import { AudioDevicesRoles, AudioDevicesRolesInternal } from 'wam-wrapper';
import { CcHubOutputDeviceInterface } from '../model/cc-hub-output-device-interface';
import { CcHubInputDeviceInterface } from '../model/cc-hub-input-device-interface';
import { getUserMediaSuccess } from '../+state/media.actions';
import { AlertService } from '../../core/audio/alert.service';
import { IrrService } from '../../core/audio/irr.service';
import { MediaTools } from '../../usb/shared/model/device';

@Injectable({
    providedIn: 'root'
})
export class CcHubAudioService {
    public static readonly CC_HUB_ID = '(0cad:1807)';
    public static readonly CC_HUB_INPUT_HEADSET_DEVICE_LABEL = 'Motorola Analog Headset 1/2 Microphone';
    public static readonly CC_HUB_OUTPUT_HEADSET_DEVICE_LABEL = 'Motorola Analog Headset 1/2 Earpiece';
    public static readonly CC_HUB_INPUT_EXTERNAL_DEVICE_LABEL = 'Motorola Analog External Inputs';
    public static readonly CC_HUB_OUTPUT_EXTERNAL_DEVICE_LABEL = 'Motorola Analog External Outputs';
    private readonly mixedLocalMediaContext = new AudioContext();
    private readonly localMediaMixerNode = new GainNode(this.mixedLocalMediaContext);
    private readonly mixedLocalDestinationNode = new MediaStreamAudioDestinationNode(this.mixedLocalMediaContext);
    private localMediaSourceNodes: MediaStreamAudioSourceNode[] = [];

    // Used to filter inputs from its own output (ie, don't play a headset mic input audio back into its own headset output)
    static readonly INPUT_FILTER_MAP: { [key: string]: string } = {
        [AudioDevicesRoles.HEADSET_1_MIC]: AudioDevicesRoles.HEADSET_1_PHONE_SPEAKER,
        [AudioDevicesRoles.HEADSET_2_MIC]: AudioDevicesRoles.HEADSET_2_PHONE_SPEAKER,
        [AudioDevicesRolesInternal.RPI_IN]: AudioDevicesRolesInternal.RPI_OUT
    };

    static readonly DEFAULT_AUDIO_OUTPUT_PERIPHERALS: (AudioDevicesRoles | AudioDevicesRolesInternal)[] = [
        AudioDevicesRoles.HEADSET_1_PHONE_SPEAKER,
        AudioDevicesRoles.HEADSET_2_PHONE_SPEAKER,
        AudioDevicesRolesInternal.RPI_OUT
    ];
    static readonly RECORDER_AUDIO_OUTPUT_PERIPHERALS: (AudioDevicesRoles | AudioDevicesRolesInternal)[] = [
        AudioDevicesRolesInternal.LLR_1,
        AudioDevicesRolesInternal.LLR_2,
        AudioDevicesRolesInternal.LLR_3
    ];

    private hs1InputDevice: CcHubInputDeviceInterface;
    private hs2InputDevice: CcHubInputDeviceInterface;
    private rpiInputDevice: CcHubInputDeviceInterface;
    private hs1OutputDevice: CcHubOutputDeviceInterface;
    private hs2OutputDevice: CcHubOutputDeviceInterface;
    private rpiOutputDevice: CcHubOutputDeviceInterface;
    private llr1OutputDevice: CcHubOutputDeviceInterface;
    private llr2OutputDevice: CcHubOutputDeviceInterface;
    private llr3OutputDevice: CcHubOutputDeviceInterface;

    private localMediaStreamsMap: { [peripheral: string]: MediaStream } = {};

    constructor(
        private store: Store,
        private mediaService: MediaService,
        private alertService: AlertService,
        private irrService: IrrService
    ) {}

    public init() {
        this.localMediaMixerNode.connect(this.mixedLocalDestinationNode);
        this.mediaService.setLocalMediaStream(this.mixedLocalDestinationNode.stream);
        this.checkContext();
        this.initAudio();
    }

    /**
     *  Initialize all CCHub audio devices
     *  **/
    private initAudio() {
        console.debug('Initializing CCHub Audio Service.');
        navigator.mediaDevices
            .getUserMedia({ audio: true, video: false })
            .then(() =>
                navigator.mediaDevices.enumerateDevices().then((devices) => {
                    let validDevices = devices.filter((d) => d.deviceId !== 'default' && d.deviceId !== 'communications');
                    let validInputDevices = validDevices.filter((d) => d.kind === 'audioinput');
                    let validOutputDevices = validDevices.filter((d) => d.kind === 'audiooutput');
                    let hsInput = validInputDevices.find((d) => d.label.includes(CcHubAudioService.CC_HUB_INPUT_HEADSET_DEVICE_LABEL));
                    let hsOutput = validOutputDevices.find((d) => d.label.includes(CcHubAudioService.CC_HUB_OUTPUT_HEADSET_DEVICE_LABEL));
                    let externalInput = validInputDevices.find((d) => d.label.includes(CcHubAudioService.CC_HUB_INPUT_EXTERNAL_DEVICE_LABEL));
                    let externalOutput = validOutputDevices.find((d) => d.label.includes(CcHubAudioService.CC_HUB_OUTPUT_EXTERNAL_DEVICE_LABEL));
                    return [hsInput, hsOutput, externalInput, externalOutput];
                })
            )
            .then(([hsInput, hsOutput, externalInput, externalOutput]) => {
                this.hs1InputDevice = new CcHubInputDeviceInterface(MediaTools.ccHubMediaDeviceInfo(hsInput, AudioDevicesRoles.HEADSET_1_MIC, 1), this.store);
                this.hs2InputDevice = new CcHubInputDeviceInterface(MediaTools.ccHubMediaDeviceInfo(hsInput, AudioDevicesRoles.HEADSET_2_MIC, 2), this.store);
                this.rpiInputDevice = new CcHubInputDeviceInterface(MediaTools.ccHubMediaDeviceInfo(externalInput, AudioDevicesRolesInternal.RPI_IN, 1), this.store);
                this.hs1OutputDevice = new CcHubOutputDeviceInterface(MediaTools.ccHubMediaDeviceInfo(hsOutput, AudioDevicesRoles.HEADSET_1_PHONE_SPEAKER, 1), this.store);
                this.hs2OutputDevice = new CcHubOutputDeviceInterface(MediaTools.ccHubMediaDeviceInfo(hsOutput, AudioDevicesRoles.HEADSET_2_PHONE_SPEAKER, 2), this.store);
                this.rpiOutputDevice = new CcHubOutputDeviceInterface(MediaTools.ccHubMediaDeviceInfo(externalOutput, AudioDevicesRolesInternal.RPI_OUT, 1), this.store);
                this.llr3OutputDevice = new CcHubOutputDeviceInterface(MediaTools.ccHubMediaDeviceInfo(externalOutput, AudioDevicesRolesInternal.LLR_3, 2), this.store);
                this.llr1OutputDevice = new CcHubOutputDeviceInterface(MediaTools.ccHubMediaDeviceInfo(externalOutput, AudioDevicesRolesInternal.LLR_1, 3), this.store);
                this.llr2OutputDevice = new CcHubOutputDeviceInterface(MediaTools.ccHubMediaDeviceInfo(externalOutput, AudioDevicesRolesInternal.LLR_2, 4), this.store);

                Promise.all([this.hs1InputDevice.init(), this.hs2InputDevice.init(), this.rpiInputDevice.init()])
                    .then(() => {
                        this.localMediaStreamsMap[AudioDevicesRoles.HEADSET_1_MIC] = this.hs1InputDevice.mediaStream;
                        this.localMediaStreamsMap[AudioDevicesRoles.HEADSET_2_MIC] = this.hs2InputDevice.mediaStream;
                        this.localMediaStreamsMap[AudioDevicesRolesInternal.RPI_IN] = this.rpiInputDevice.mediaStream;
                        this.localMediaSourceNodes = [this.hs1InputDevice.mediaStream, this.hs2InputDevice.mediaStream, this.rpiInputDevice.mediaStream]
                            .map((mediaStream) => new MediaStreamAudioSourceNode(this.mixedLocalMediaContext, { mediaStream }));
                        this.localMediaSourceNodes.forEach((node) => node.connect(this.localMediaMixerNode));
                    })
                    .then(() => {
                        [this.hs1OutputDevice, this.hs2OutputDevice, this.rpiOutputDevice, this.llr3OutputDevice, this.llr1OutputDevice, this.llr2OutputDevice].forEach((output) =>
                            output.init().then(() => {
                                output.setRemoteMediaStream(this.mediaService.remoteMediaStream$.value);
                                output.setSharedLocalMicrophoneMediaStreams(Object.entries(this.localMediaStreamsMap)
                                    .filter(([inputDevice]) => CcHubAudioService.INPUT_FILTER_MAP[inputDevice] !== output.peripheral)
                                    .map(([, mediaStream]) => mediaStream));
                                output.setAlertMediaStream(this.alertService.mediaStream);
                                output.setIrrMediaStream(this.irrService.mediaStream);
                            })
                        );
                    })
                    .then(() => this.store.dispatch(getUserMediaSuccess()))
                    .then(() => this.monitorMediaStreams())
                    .catch((e) => console.error('Failed to initialize CCHub Audio Service', e));
            });
    }

    private monitorMediaStreams() {
        this.mediaService.remoteMediaStream$.subscribe((remoteMediaStream) =>
            [this.hs1OutputDevice, this.hs2OutputDevice, this.rpiOutputDevice, this.llr3OutputDevice, this.llr1OutputDevice, this.llr2OutputDevice].forEach((device) => device.setRemoteMediaStream(remoteMediaStream))
        );
    }

    public checkContext() {
        console.debug(`Checking cc-hub combined input audio context: ${this.mixedLocalMediaContext.state}`);
        if (this.mixedLocalMediaContext.state === 'suspended') {
            console.warn(`Cc-hub combined input audio context is suspended. Attempting to resume.`);
            this.mixedLocalMediaContext
                .resume()
                .then(() => console.warn(`Successfully started cc-hub combined input audio context`))
                .catch((err) => console.error(`Unable to start cc-hub combined input audio context {}`, err));
        }
    }
}
